// Imposition engine: compute how pieces fit on the printable area of a page,
// how many pages are needed, and the centered block origin.
// All values in mm.

// Maximum copies that fit a single sheet (perPage) with given settings.
export function maxCopiesThatFit({ page, piece, separation, bleed }) {
  const layout = computeLayout({ page, piece, copies: 9999, separation, bleed });
  return layout.perPage;
}

export function computeLayout({ page, piece, copies, separation, bleed }) {
  const pieceW = piece.width_mm + bleed * 2;
  const pieceH = piece.height_mm + bleed * 2;
  const sepH = Math.max(0, separation.h_mm);
  const sepV = Math.max(0, separation.v_mm);

  // Number of columns/rows that fit in the printable area.
  // Formula: floor((printable + sep) / (piece + sep))
  const cols = Math.max(
    0,
    Math.floor((page.printable_w_mm + sepH) / (pieceW + sepH))
  );
  const rows = Math.max(
    0,
    Math.floor((page.printable_h_mm + sepV) / (pieceH + sepV))
  );

  const perPage = cols * rows;
  const pages = perPage > 0 ? Math.ceil(copies / perPage) : 0;

  // Total block size occupied by all pieces with separations.
  const blockW = cols > 0 ? cols * pieceW + (cols - 1) * sepH : 0;
  const blockH = rows > 0 ? rows * pieceH + (rows - 1) * sepV : 0;

  // Origin of the printable area on the paper (centered: paper margin = (paper - printable)/2)
  const printableX = (page.width_mm - page.printable_w_mm) / 2;
  const printableY = (page.height_mm - page.printable_h_mm) / 2;

  // Origin of the block: centered within the printable area.
  const blockX = printableX + (page.printable_w_mm - blockW) / 2;
  const blockY = printableY + (page.printable_h_mm - blockH) / 2;

  return {
    cols,
    rows,
    perPage,
    pages,
    pieceW, // with bleed
    pieceH, // with bleed
    blockW,
    blockH,
    blockX,
    blockY,
    printableX,
    printableY,
    sepH,
    sepV,
  };
}

// Produce the list of slot positions for a given page index (0-based).
// Each slot has x/y origin (top-left of the piece WITHOUT bleed expansion, i.e., the visual cut frame).
export function slotsForPage({ page, piece, copies, separation, bleed, pageIndex }) {
  const layout = computeLayout({ page, piece, copies, separation, bleed });
  if (layout.perPage === 0) return { slots: [], layout };

  const startIndex = pageIndex * layout.perPage;
  const endIndex = Math.min(startIndex + layout.perPage, copies);

  const slots = [];
  for (let globalIdx = startIndex; globalIdx < endIndex; globalIdx++) {
    const localIdx = globalIdx - startIndex;
    const col = localIdx % layout.cols;
    const row = Math.floor(localIdx / layout.cols);
    // Block origin is in "with bleed" coordinates. The visible cut frame is offset by `bleed` mm.
    const xBleed = layout.blockX + col * (layout.pieceW + layout.sepH);
    const yBleed = layout.blockY + row * (layout.pieceH + layout.sepV);
    slots.push({
      index: globalIdx,
      // Cut frame coordinates (top-left of the piece, no bleed expansion):
      x_mm: xBleed + bleed,
      y_mm: yBleed + bleed,
      // Bleed rectangle coordinates (outer):
      bleed_x_mm: xBleed,
      bleed_y_mm: yBleed,
      width_mm: piece.width_mm,
      height_mm: piece.height_mm,
      bleed_w_mm: piece.width_mm + bleed * 2,
      bleed_h_mm: piece.height_mm + bleed * 2,
    });
  }
  return { slots, layout };
}
